import { KfsInputs, KfsCalculatedValues, KFS_CONSTANTS } from '../types/kfs.types';

const { INSURANCE_RATE_PER_GRAM, INSURANCE_TENURE_MONTHS, FIXED_PROCESSING_FEE, INSTALMENT_INTERVALS } = KFS_CONSTANTS;

/** Field 6: Insurance Charge = ROUND(grossWeight × 1, 0) */
export function calcInsuranceCharge(grossWeight: number): number {
  return Math.round(grossWeight * INSURANCE_RATE_PER_GRAM);
}

/** Field 11: Total Interest = loanAmount × (interestRate / 100) */
export function calcTotalInterest(loanAmount: number, interestRatePct: number): number {
  return loanAmount * (interestRatePct / 100);
}

/** Field 12: Fee Charges = ROUND((insuranceCharge × 12) + 59, 0) */
export function calcFeeCharges(insuranceCharge: number): number {
  return Math.round((insuranceCharge * INSURANCE_TENURE_MONTHS) + FIXED_PROCESSING_FEE);
}

/** Fields 7 & 16: APR = interestRate + (feeCharges / loanAmount) — returned as decimal */
export function calcAPR(interestRatePct: number, feeCharges: number, loanAmount: number): number {
  return (interestRatePct / 100) + (feeCharges / loanAmount);
}

/** Field 14: Net Disbursement = loanAmount − 59 */
export function calcNetDisbursement(loanAmount: number): number {
  return loanAmount - FIXED_PROCESSING_FEE;
}

/** Field 15: Total Amount Payable = loanAmount + totalInterest + feeCharges */
export function calcTotalAmountPayable(loanAmount: number, totalInterest: number, feeCharges: number): number {
  return loanAmount + totalInterest + feeCharges;
}

/** Instalment due dates: loanDate + 89/179/269/359 days */
export function calcInstalmentDueDates(loanDate: Date): Date[] {
  return INSTALMENT_INTERVALS.map((days) => {
    const date = new Date(loanDate);
    date.setDate(date.getDate() + days);
    return date;
  });
}

/** Orchestrator: calculates all KFS values. Returns null if inputs are incomplete. */
export function calculateAll(inputs: KfsInputs): KfsCalculatedValues | null {
  const { loanAmount, loanDate, interestRate, grossWeight } = inputs;

  if (loanAmount == null || loanDate == null || interestRate == null || grossWeight == null) {
    return null;
  }
  if (loanAmount <= 0 || interestRate <= 0 || grossWeight <= 0) {
    return null;
  }

  const insuranceCharge = calcInsuranceCharge(grossWeight);
  const totalInterest = calcTotalInterest(loanAmount, interestRate);
  const feeCharges = calcFeeCharges(insuranceCharge);
  const apr = calcAPR(interestRate, feeCharges, loanAmount);
  const netDisbursement = calcNetDisbursement(loanAmount);
  const totalAmountPayable = calcTotalAmountPayable(loanAmount, totalInterest, feeCharges);
  const instalmentDueDates = calcInstalmentDueDates(loanDate);

  return {
    insuranceCharge,
    apr,
    totalInterest,
    feeCharges,
    payableToRE: feeCharges,
    netDisbursement,
    totalAmountPayable,
    instalmentDueDates,
  };
}

/** Format amount in Indian Rupee format: ₹X,XX,XXX */
export function formatINR(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Format APR as percentage with 3 decimal places */
export function formatAPR(value: number): string {
  return (value * 100).toFixed(3) + '%';
}
